import { useMemo } from "react";
import { Card, Flex, Typography, Spin } from "antd";

const { Title, Text } = Typography;

export default function TableSelectorPanel({
  eventTables,
  loading,
  selectedTables,
  onSelectionChange,
}) {
  const selectedKeys = useMemo(
    () => new Set(selectedTables.map((t) => `${t.event_table_id}-${t.table_number}`)),
    [selectedTables]
  );

  const runningTotal = useMemo(
    () => selectedTables.reduce((sum, t) => sum + (Number(t.price) || 0), 0),
    [selectedTables]
  );

  const toggleTable = (tableType, tableNumber) => {
    const key = `${tableType.id}-${tableNumber}`;
    if (selectedKeys.has(key)) {
      onSelectionChange(
        selectedTables.filter(
          (t) => !(t.event_table_id === tableType.id && t.table_number === tableNumber)
        )
      );
      return;
    }

    onSelectionChange([
      ...selectedTables,
      {
        event_table_id: tableType.id,
        table_number: tableNumber,
        label: tableType.label,
        persons_count: tableType.persons_count,
        price: tableType.price,
      },
    ]);
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 200 }}>
        <Spin size="large" tip="Loading tables..." />
      </Flex>
    );
  }

  if (!eventTables?.length) {
    return (
      <Card className="orderSummaryCard">
        <Text>No table options are available for this event.</Text>
      </Card>
    );
  }

  return (
    <Flex vertical gap={16} className="ticketBookingCards" style={{ width: "100%" }}>
      {eventTables.map((tableType) => {
        const availableNumbers = tableType.available_numbers || [];
        const allNumbers = Array.from({ length: tableType.quantity || 0 }, (_, i) => i + 1);

        return (
          <Card key={tableType.id} className="orderSummaryCard">
            <Flex vertical gap={12}>
              <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12}>
                <div>
                  <Title level={4} style={{ margin: 0 }}>{tableType.label}</Title>
                  <Text type="secondary">
                    {tableType.persons_count} persons per table · {tableType.available_count ?? availableNumbers.length} available
                  </Text>
                </div>
                <Text className="orderPrice">₹{tableType.price}</Text>
              </Flex>

              <Flex wrap="wrap" gap={8}>
                {allNumbers.map((num) => {
                  const isAvailable = availableNumbers.includes(num);
                  const isSelected = selectedKeys.has(`${tableType.id}-${num}`);

                  return (
                    <button
                      key={`${tableType.id}-${num}`}
                      type="button"
                      disabled={!isAvailable}
                      onClick={() => isAvailable && toggleTable(tableType, num)}
                      style={{
                        minWidth: 72,
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: isSelected ? "2px solid #1677ff" : "1px solid #d9d9d9",
                        background: !isAvailable ? "#f5f5f5" : isSelected ? "#e6f4ff" : "#fff",
                        color: !isAvailable ? "#bfbfbf" : isSelected ? "#1677ff" : "#262626",
                        cursor: isAvailable ? "pointer" : "not-allowed",
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    >
                      Table #{num}
                    </button>
                  );
                })}
              </Flex>
            </Flex>
          </Card>
        );
      })}

      <Card className="orderSummaryCard">
        <Flex justify="space-between" align="center">
          <Text strong>Selected tables total</Text>
          <Text strong className="orderPrice">₹{runningTotal}</Text>
        </Flex>
      </Card>
    </Flex>
  );
}
